import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, CheckCircle2, Loader2, KeyRound, ArrowRight, UserPlus, LogIn } from 'lucide-react';

/**
 * Page /rejoindre?code=ABC123
 * Deep-link magique : quand un moniteur envoie son code par SMS/WhatsApp,
 * l'élève arrive ici avec le code pré-rempli. Si déjà connecté → rattachement direct.
 * Sinon → prompt login/signup avec redirection auto vers rattachement après.
 */
export default function JoinTeacherPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, openLogin, openRegister } = useAuth();
  const rawCode = (searchParams.get('code') || '').toUpperCase().trim();
  const [code, setCode] = useState(rawCode);
  const [status, setStatus] = useState(rawCode ? 'ready' : 'input'); // input | ready | linking | success | error
  const [message, setMessage] = useState('');
  const [teacherName, setTeacherName] = useState('');

  // Persiste le code + return URL pour que le rattachement se fasse après inscription
  const persistReturnUrl = (c) => {
    try {
      localStorage.setItem('cv_auth_next', `/rejoindre?code=${encodeURIComponent(c)}`);
    } catch (_) { /* silent */ }
  };

  const openSignup = () => {
    persistReturnUrl(rawCode || code);
    openRegister();
  };
  const openLoginModal = () => {
    persistReturnUrl(rawCode || code);
    openLogin();
  };

  // Auto-attach dès qu'on est connecté avec code valide
  useEffect(() => {
    if (authLoading) return;
    if (user && rawCode && status === 'ready') {
      doLink(rawCode);
    }
  }, [authLoading, user, rawCode]);

  const doLink = async (c) => {
    setStatus('linking');
    try {
      const r = await api.linkTeacher(c.trim().toUpperCase());
      setTeacherName(r.teacher_name || 'ton enseignant');
      setStatus('success');
      setMessage(`Rattachement réussi ! Tu es maintenant lié(e) à ${r.teacher_name || 'ton enseignant ECSR'}.`);
      // Nettoie l'éventuel next mémorisé pour éviter une redirection en double
      try { localStorage.removeItem('cv_auth_next'); } catch (_) { /* silent */ }
      setTimeout(() => navigate('/eleve/dashboard'), 1800);
    } catch (err) {
      setStatus('error');
      setMessage(err?.response?.data?.detail || 'Code invalide ou expiré.');
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      // Redirige vers login avec return_url
      const returnUrl = `/rejoindre?code=${encodeURIComponent(code)}`;
      navigate(`/?login=1&next=${encodeURIComponent(returnUrl)}`);
      return;
    }
    doLink(code);
  };

  return (
    <PageShell>
      <section className="max-w-2xl mx-auto px-4 lg:px-6 py-10 sm:py-14" data-testid="join-teacher-page">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Rejoindre mon enseignant</span>
        </nav>

        <SectionTitle
          kicker="Rattachement enseignant · en 2 clics"
          title="Rejoindre ton enseignant"
        />
        <p className="mt-4 text-white/70 text-[14.5px] leading-relaxed">
          Ton moniteur t&apos;a envoyé un code par SMS ou WhatsApp ? Renseigne-le ici. Tu pourras suivre ta progression, tes séances et tes bilans en temps réel.
        </p>

        <div className="mt-8 rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-950/25 via-[#0d0d0e] to-[#0a0a0b] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-[0.07] pointer-events-none">
            <GraduationCap size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            {status === 'success' ? (
              <div className="text-center py-6" data-testid="join-success">
                <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
                <h3 className="font-serif-editorial text-white text-[24px] leading-tight">Rattachement réussi</h3>
                <p className="mt-2 text-white/70 text-[14px]">Tu es maintenant lié(e) à <span className="text-emerald-300 font-semibold">{teacherName}</span>.</p>
                <p className="mt-4 text-white/50 text-[12.5px]">Redirection vers ton dashboard élève…</p>
              </div>
            ) : status === 'linking' ? (
              <div className="text-center py-6">
                <Loader2 size={36} className="text-red-400 mx-auto mb-4 animate-spin" />
                <p className="text-white/70 text-[14px]">Rattachement en cours…</p>
              </div>
            ) : !user && !authLoading ? (
              <>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                  <KeyRound size={11} /> Étape 1/2 · Connexion ou inscription
                </div>
                <h3 className="mt-4 font-serif-editorial text-white text-[22px]">Ton code est prêt : <span className="text-red-400 font-mono-spec">{rawCode || '—'}</span></h3>
                <p className="mt-2 text-white/70 text-[13.5px]">Crée un compte gratuit ou connecte-toi pour que le rattachement fonctionne. Ton code sera conservé automatiquement.</p>
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={openSignup}
                    data-testid="join-signup"
                    className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold inline-flex items-center justify-center gap-2 transition"
                  >
                    <UserPlus size={14} /> Créer un compte gratuit
                  </button>
                  <button
                    type="button"
                    onClick={openLoginModal}
                    data-testid="join-login"
                    className="flex-1 h-11 rounded-full border border-white/20 text-white hover:border-white/40 text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition"
                  >
                    <LogIn size={14} /> J&apos;ai déjà un compte
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={onSubmit}>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                  <KeyRound size={11} /> Code de rattachement
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="EX : AB12CD34"
                    data-testid="join-code-input"
                    className="w-full h-14 px-5 rounded-xl bg-[#0a0a0b] border border-white/15 text-white text-center font-mono-spec text-[24px] tracking-[0.2em] focus:outline-none focus:border-red-500 uppercase"
                    autoComplete="off"
                    autoFocus={!rawCode}
                  />
                </div>
                {status === 'error' && (
                  <div className="mt-3 text-red-300 text-[12.5px] text-center" data-testid="join-error">{message}</div>
                )}
                <button
                  type="submit"
                  disabled={!code.trim() || status === 'linking'}
                  data-testid="join-submit"
                  className="mt-5 w-full h-12 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 transition"
                >
                  Rejoindre mon enseignant <ArrowRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-red-400 text-[11px] font-mono-spec uppercase tracking-wider font-bold">1</div>
            <div className="mt-1.5 text-white text-[13.5px]">Ton moniteur t&apos;a envoyé un code par SMS ou WhatsApp</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-red-400 text-[11px] font-mono-spec uppercase tracking-wider font-bold">2</div>
            <div className="mt-1.5 text-white text-[13.5px]">Tu crées un compte gratuit (30 s)</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-red-400 text-[11px] font-mono-spec uppercase tracking-wider font-bold">3</div>
            <div className="mt-1.5 text-white text-[13.5px]">Tu vois ta progression en temps réel</div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
